/**
 * Copyright 2014 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { alDefineObjectProperties, AVM1NativeFunction } from '../runtime';
import { isNullOrUndefined, Debug, release } from '@awayfl/swf-loader';
import { AVM1ArrayNative } from '../natives';
import { TextField, MovieClip } from '@awayjs/scene';
import { AVM1Object } from '../runtime/AVM1Object';
import { AVM1Function } from '../runtime/AVM1Function';
import { AVM1PropertyDescriptor } from '../runtime/AVM1PropertyDescriptor';
import { ClipEventMappings, AVM1EventProps, AVM1KeyCodeMap } from './AVM1EventHandler';
export var DEPTH_OFFSET = 16384;
/**
 * Checks if an object contains a reference to a native AS3 object.
 * Returns false for MovieClip instances or instances of constructors with
 * MovieClip on their prototype chain that were created in script using,
 * e.g. new MovieClip(). Those lack the part of their internal structure
 * that makes them displayable.
 */
export function hasAwayJSAdaptee(obj) {
    return !!obj.adaptee;
}
/**
 * Returns obj's reference to a native AS3 object. If the reference
 * doesn't exist, obj was created in script, e.g. with new MovieClip(),
 * and doesn't reflect a real, displayable display object. In that case,
 * an empty null-proto object is created and returned. This is used for
 * classes that are linked to embedded symbols that extend MovieClip. Their
 * inheritance chain is built by assigning new MovieClip to their prototype.
 * When a proper, displayable, instance of such a class is created via
 * attachMovie, initial values for properties such as tabEnabled
 * can be initialized from values set on the template object.
 */
export function getAwayObjectOrTemplate(obj) {
    if (obj.adaptee) {
        return obj.adaptee;
    }
    // The _as3ObjectTemplate is not really an ASObject type, but we will fake
    // that for AVM1SymbolBase's properties transfers.
    if (!obj._as3ObjectTemplate) {
        var template = void 0;
        var proto = obj.alPrototype;
        while (proto && !proto.initAVM1SymbolInstance) {
            template = proto._as3ObjectTemplate;
            if (template) {
                break;
            }
            proto = proto.alPrototype;
        }
        obj._as3ObjectTemplate = Object.create(template || null);
    }
    return obj._as3ObjectTemplate;
}
export var BlendModesMap = [undefined, 'normal', 'layer', 'multiply',
    'screen', 'lighten', 'darken', 'difference', 'add', 'subtract', 'invert',
    'alpha', 'erase', 'overlay', 'hardlight'];
export function avm1HasEventProperty(context, target, propertyName) {
    if (target.alHasProperty(propertyName) &&
        (target.alGet(propertyName) instanceof AVM1Function)) {
        return true;
    }
    if (target.alHasProperty(propertyName) &&
        (target._ownProperties[propertyName] && target._ownProperties[propertyName].value)) {
        return true;
    }
    var listenersField = target.alGet('_listeners');
    if (!(listenersField instanceof AVM1ArrayNative)) {
        return false;
    }
    var listeners = listenersField.value;
    return listeners.some(function (listener) {
        return (listener instanceof AVM1Object) && listener.alHasProperty(propertyName);
    });
}
export function avm1BroadcastNativeEvent(context, target, propertyName, args) {
    //console.log("avm1BroadcastNativeEvent", propertyName)
    if (args === void 0) { args = null; }
    var handler = target.alGet(propertyName);
    if (handler instanceof AVM1Function) {
        if (propertyName.toLowerCase() == 'onenterframe')
            handler.isOnEnter = true;
        context.executeFunction(handler, target, args);
    }
    var _listeners = target.alGet('_listeners');
    if (_listeners instanceof AVM1ArrayNative) {
        var handlerOnListener_1 = null;
        _listeners.value.forEach(function (listener) {
            if (!(listener instanceof AVM1Object)) {
                return;
            }
            handlerOnListener_1 = listener.alGet(propertyName);
            if (handlerOnListener_1 instanceof AVM1Function) {
                context.executeFunction(handlerOnListener_1, target, args);
            }
        });
    }
}
export function avm1BroadcastEvent(context, target, propertyName, args) {
    if (args === void 0) { args = null; }
    var handler = target.alGet(propertyName);
    if (handler instanceof AVM1Function) {
        handler.alCall(target, args);
    }
    var _listeners = target.alGet('_listeners');
    if (_listeners instanceof AVM1ArrayNative) {
        var handlerOnListener_2 = null;
        _listeners.value.forEach(function (listener) {
            if (!(listener instanceof AVM1Object)) {
                return;
            }
            handlerOnListener_2 = listener.alGet(propertyName);
            if (handlerOnListener_2 instanceof AVM1Function) {
                handlerOnListener_2.alCall(listener, args);
            }
        });
    }
}
var myCount = 0;
function createAVM1NativeObject(ctor, nativeObject, context) {
    // We need to walk on __proto__ to find right ctor.prototype.
    var template;
    var proto = ctor.alGetPrototypeProperty();
    while (proto && !proto.initAVM1SymbolInstance) {
        if (proto._as3ObjectTemplate && !template) {
            template = proto._as3ObjectTemplate;
        }
        proto = proto.alPrototype;
    }
    release || Debug.assert(proto);
    var avm1Object = Object.create(proto);
    proto.initAVM1SymbolInstance.call(avm1Object, context, nativeObject);
    avm1Object.alPrototype = ctor.alGetPrototypeProperty();
    avm1Object.alSetOwnConstructorProperty(ctor);
    nativeObject._as2Object = avm1Object;
    ctor.alCall(avm1Object);
    avm1Object.aCount = myCount++;
    //	80pro: creating a new _ownProperties
    //  makes sure that newly added properties are added to instance, not to prototype
    //avm1Object._ownProperties={};
    //avm1Object._ownProperties = Object.create(null);
    if (template) {
        // transfer properties from the template
        for (var prop in template) {
            nativeObject[prop] = template[prop];
        }
    }
    return avm1Object;
}
export function getAVM1Object(awayObject, context) {
    if (!awayObject) {
        return null;
    }
    if (awayObject._as2Object) {
        return awayObject._as2Object;
    }
    var avmObject;
    if (awayObject.isAsset(MovieClip)) {
        if (awayObject.timeline.isButton) {
            avmObject = createAVM1NativeObject(context.globals.Button, awayObject, context);
        }
        else {
            avmObject = createAVM1NativeObject(context.globals.MovieClip, awayObject, context);
        }
    }
    else if (awayObject.isAsset(TextField)) {
        avmObject = createAVM1NativeObject(context.globals.TextField, awayObject, context);
    }
    if (avmObject) {
        awayObject._as2Object = avmObject;
        awayObject.adapter = avmObject;
        avmObject.adaptee = awayObject;
    }
    awayObject._as2Object = avmObject;
    return avmObject;
}
export function wrapAVM1NativeMembers(context, wrap, obj, members, prefixFunctions) {
    if (prefixFunctions === void 0) { prefixFunctions = false; }
    function wrapFunction(fn) {
        if (isNullOrUndefined(fn)) {
            return undefined;
        }
        release || Debug.assert(typeof fn === 'function');
        if (!prefixFunctions) {
            return new AVM1NativeFunction(context, fn);
        }
        return new AVM1NativeFunction(context, function () {
            var args = Array.prototype.slice.call(arguments, 0);
            args.unshift(context);
            return fn.apply(this, args);
        });
    }
    function getMemberDescriptor(memberName) {
        var desc;
        for (var p = obj; p; p = Object.getPrototypeOf(p)) {
            desc = Object.getOwnPropertyDescriptor(p, memberName);
            if (desc) {
                return desc;
            }
        }
        return null;
    }
    if (!members) {
        return;
    }
    members.forEach(function (memberName) {
        if (memberName[memberName.length - 1] === '#') {
            // Property mapping
            var getterName = 'get' + memberName[0].toUpperCase() + memberName.slice(1, -1);
            var getter = obj[getterName];
            var setterName = 'set' + memberName[0].toUpperCase() + memberName.slice(1, -1);
            var setter = obj[setterName];
            release || Debug.assert(getter || setter, 'define getter or setter');
            var desc_1 = new AVM1PropertyDescriptor(128 /* AVM1PropertyFlags.ACCESSOR */ |
                2 /* AVM1PropertyFlags.DONT_DELETE */ |
                1 /* AVM1PropertyFlags.DONT_ENUM */, null, wrapFunction(getter), wrapFunction(setter));
            wrap.alSetOwnProperty(memberName.slice(0, -1), desc_1);
            return;
        }
        var nativeDesc = getMemberDescriptor(memberName);
        if (!nativeDesc) {
            return;
        }
        if (nativeDesc.get || nativeDesc.set) {
            release || Debug.assert(false, 'Redefine ' + memberName + ' property getter/setter as functions');
            return;
        }
        var value = nativeDesc.value;
        if (typeof value === 'function') {
            value = wrapFunction(value);
        }
        var desc = new AVM1PropertyDescriptor(64 /* AVM1PropertyFlags.DATA */ |
            2 /* AVM1PropertyFlags.DONT_DELETE */ |
            1 /* AVM1PropertyFlags.DONT_ENUM */, value);
        wrap.alSetOwnProperty(memberName, desc);
    });
}
export function wrapAVM1NativeClass(context, wrapAsFunction, cls, staticMembers, members, call, cstr) {
    var wrappedFn = wrapAsFunction ?
        new AVM1NativeFunction(context, call || function () { }, function () {
            // Creating simple AVM1 object
            var obj = new cls(context);
            obj.alPrototype = wrappedPrototype;
            obj.alSetOwnConstructorProperty(wrappedFn);
            if (cstr) {
                cstr.apply(obj, arguments);
            }
            return obj;
        }) :
        new AVM1Object(context);
    wrapAVM1NativeMembers(context, wrappedFn, cls, staticMembers, true);
    var wrappedPrototype = new cls(context);
    wrappedPrototype.alPrototype = context.builtins.Object.alGetPrototypeProperty();
    wrapAVM1NativeMembers(context, wrappedPrototype, cls.prototype, members, false);
    alDefineObjectProperties(wrappedFn, {
        prototype: {
            value: wrappedPrototype
        }
    });
    alDefineObjectProperties(wrappedPrototype, {
        constructor: {
            value: wrappedFn,
            writable: true
        }
    });
    return wrappedFn;
}
export function initializeAVM1Object(awayObject, context, placeObjectTag) {
    var instanceAVM1 = getAVM1Object(awayObject, context);
    release || Debug.assert(instanceAVM1);
    if (placeObjectTag.variableName) {
        instanceAVM1.alPut('variable', placeObjectTag.variableName);
    }
    var events = placeObjectTag.events;
    if (!events) {
        return;
    }
    var swfEvent;
    var actionsData;
    var handler;
    var flags;
    var eventFlag;
    var eventMapping;
    var eventName;
    var eventProps = null;
    for (var j = 0; j < events.length; j++) {
        swfEvent = events[j];
        actionsData;
        if (swfEvent.actionsBlock) {
            actionsData = context.actionsDataFactory.createActionsData(swfEvent.actionsBlock, 's' + placeObjectTag.symbolId + 'd' + placeObjectTag.depth + 'e' + j);
            swfEvent.actionsBlock = null;
            swfEvent.compiled = actionsData;
        }
        else {
            actionsData = swfEvent.compiled;
        }
        release || Debug.assert(actionsData);
        handler = clipEventHandler.bind(null, actionsData, instanceAVM1);
        flags = swfEvent.flags;
        for (var key in ClipEventMappings) {
            eventFlag = parseInt(key);
            eventFlag |= 0;
            if (!(flags & (eventFlag | 0))) {
                continue;
            }
            else if (eventFlag == 262144 /* AVM1ClipEvents.Construct */) {
                awayObject.onConstruct = handler;
                continue;
            }
            else if (eventFlag == 512 /* AVM1ClipEvents.Initialize */) {
                awayObject.onInitialize = handler;
                continue;
            }
            else if (eventFlag == 1 /* AVM1ClipEvents.Load */) {
                awayObject.onLoaded = handler;
                continue;
            }
            eventMapping = ClipEventMappings[eventFlag];
            eventName = eventMapping.eventName;
            //console.log("eventName", eventName, eventMapping, eventFlag, swfEvent);
            if (!eventName) {
                Debug.warning('ClipEvent: ' + eventFlag + ' not implemented');
                continue;
            }
            if (swfEvent.keyCode) {
                eventProps = new AVM1EventProps();
                eventProps.keyCode = swfEvent.keyCode;
                if (swfEvent.keyCode < 32 && AVM1KeyCodeMap[swfEvent.keyCode])
                    eventProps.keyCode = AVM1KeyCodeMap[swfEvent.keyCode];
            }
            instanceAVM1._addOnClipEventListener(eventMapping, handler, eventProps);
        }
    }
}
export function toTwipFloor(value) {
    // in theory this should do:
    //return Math.round(value*20)/20;
    // because AwayJS does not use big.js internally, floats might have this nasty rounding error
    // we need to floor twips, and add a additional twip in case it had the floating error
    var isNeg = value < 0 ? -1 : 1;
    value = Math.abs(value);
    var twip = Math.floor(value * 20) / 20;
    if (value > twip && (value - twip) > 0.04) {
        twip += 0.05;
        // the addition might introduce float issue again,
        // so make sure go back to twips
        twip = Math.round(twip * 20) / 20;
    }
    return twip * isNeg;
}
export function toTwipRound(value) {
    return Math.round(value * 20) / 20;
}
export function avm2AwayDepth(value) {
    return value + 1;
}
export function away2avmDepth(value) {
    return value - 1;
}
function clipEventHandler(actionsData, receiver) {
    return receiver.context.executeActions(actionsData, receiver);
}
